// src/components/hooks/useCalendarSettings.ts
import { useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

/* ============================================================
   🔹 Tipos
============================================================ */
export interface CalendarSettings {
  mostrar_trabajador: boolean;
  mostrar_servicio: boolean;
  mostrar_telefono: boolean;
  mostrar_email: boolean;
  mostrar_tipo_pago: boolean;
  mostrar_iconos: boolean;
}

export const defaultCalendarSettings: CalendarSettings = {
  mostrar_trabajador: true,
  mostrar_servicio: false,
  mostrar_telefono: false,
  mostrar_email: false,
  mostrar_tipo_pago: false,
  mostrar_iconos: true,
};

/* ============================================================
   🔹 Hook principal
============================================================ */
export const useCalendarSettings = (idUsuario: string | null) => {
  const [settings, setSettings] = useState<CalendarSettings>(
    defaultCalendarSettings
  );
  const [loading, setLoading] = useState(false);

  /* ============================================================
     🔹 Obtener configuración
  ============================================================ */
  const fetchSettings = useCallback(async () => {
    if (!idUsuario) return;

    setLoading(true);

    const { data, error } = await supabase
      .from('configuracion_vista_cita')
      .select('*')
      .eq('id_usuario', idUsuario)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') {
      Sentry.captureException(error, { extra: { idUsuario } }); // 👈
      toast.error('Error al cargar ajustes');
      console.error(error);
    }

    setSettings({
      mostrar_trabajador: data?.mostrar_trabajador ?? true,
      mostrar_servicio: data?.mostrar_servicio ?? false,
      mostrar_telefono: data?.mostrar_telefono ?? false,
      mostrar_email: data?.mostrar_email ?? false,
      mostrar_tipo_pago: data?.mostrar_tipo_pago ?? false,
      mostrar_iconos: data?.mostrar_iconos ?? true,
    });

    setLoading(false);
  }, [idUsuario]);

  /* ============================================================
     🔹 Guardar configuración
  ============================================================ */
  const saveSettings = useCallback(
    async (values: CalendarSettings) => {
      if (!idUsuario) return { ok: false, error: 'Usuario vacío' };

      setLoading(true);

      const { error } = await supabase.from('configuracion_vista_cita').upsert({
        id_usuario: idUsuario,
        ...values,
      });

      setLoading(false);

      if (error) {
        Sentry.captureException(error, { extra: { idUsuario } }); // 👈
        toast.error('No se pudieron guardar los ajustes');
        console.error(error);
        return { ok: false };
      }

      toast.success('Ajustes guardados.');
      setSettings(values);

      return { ok: true };
    },
    [idUsuario]
  );

  /* ============================================================
     🔹 Auto-carga inicial
  ============================================================ */
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  /* ============================================================
     🔹 API pública
  ============================================================ */
  return {
    settings,
    setSettings,
    loading,
    saveSettings,
    fetchSettings,
  };
};
